// The needs-attention queue, shared by the Dashboard and the nav badge (same
// query keys, so one set of requests).
//
//   server items     GET /platform/dashboard → attention, minus its 30-day
//                    `inactive`
//   inactive (14d)   tenants with no league-admin activity for 14+ days,
//                    from the tenant list's last_activity_at (super-admins
//                    excluded)
//   new inquiries    every inquiry still `new` (owner, November 3): Soon,
//                    and Urgent once it has waited 2 days

import { api, inactiveAttention, sortAttention, type AttentionItem, type DashboardResponse } from '@/api'
import { useQuery } from './query'

/** An inquiry nobody has picked up yet. Not a tenant: it links to the inquiry. */
export interface InquiryAttention {
  kind: 'new_inquiry'
  severity: AttentionItem['severity']
  inquiry: { id: number; name: string; topic: string }
  message: string
  at: string
}

export type QueueItem = AttentionItem | InquiryAttention

/** How long a new inquiry waits before it's Urgent. */
export const INQUIRY_URGENT_DAYS = 2
/** New inquiries read for the queue (the newest). */
const INQUIRY_LIMIT = 50
const SEVERITY_RANK = { high: 0, medium: 1, low: 2 } as const

export function inquiryAttention(rows: Array<{ id: number; name: string | null; email: string; topic: string; message: string | null; created_at: string }>, now = Date.now()): InquiryAttention[] {
  return rows.map((i) => ({
    kind: 'new_inquiry',
    severity: now - new Date(i.created_at).getTime() >= INQUIRY_URGENT_DAYS * 86_400_000 ? 'high' : 'medium',
    inquiry: { id: i.id, name: i.name || i.email, topic: i.topic },
    message: i.message ? i.message.replace(/\s+/g, ' ').trim() : 'No message',
    at: i.created_at,
  }))
}

const titleOf = (i: QueueItem) => ('tenant' in i ? i.tenant.name : i.inquiry.name)

/** Severity first, then oldest first (no date last), then by name: sortAttention's order, for both kinds. */
export function sortQueue(items: QueueItem[]): QueueItem[] {
  const at = (i: QueueItem) => i.at ?? '\uffff'
  return [...items].sort((x, y) => SEVERITY_RANK[x.severity] - SEVERITY_RANK[y.severity] || at(x).localeCompare(at(y)) || titleOf(x).localeCompare(titleOf(y)))
}

export interface AttentionQueue {
  items: QueueItem[]
  dashboard: DashboardResponse | undefined
  status: 'loading' | 'success' | 'error'
  error: unknown
  refetch: () => void
}

export function useAttentionQueue(): AttentionQueue {
  const dash = useQuery(['dashboard'], (signal) => api.dashboard.get(signal))
  const list = useQuery(['tenants', 'all'], (signal) => api.tenants.list({ page_size: 100, sort: '-last_activity_at' }, signal))
  const inquiries = useQuery(['inquiries', 'attention'], (signal) => api.growth.inquiries.list({ status: 'new', page_size: INQUIRY_LIMIT }, signal))

  let items: QueueItem[] = []
  if (dash.data) {
    const serverItems = dash.data.attention.filter((i) => i.kind !== 'inactive')
    const inactive = inactiveAttention(
      (list.data?.leagues ?? []).map((t) => ({ tenant: { id: t.id, name: t.name, subdomain: t.subdomain }, status: t.account.status, last_activity_at: t.last_activity_at, created_at: t.created_at })),
    )
    items = sortQueue([...sortAttention([...serverItems, ...inactive]), ...inquiryAttention(inquiries.data?.inquiries ?? [])])
  }

  const status = dash.status === 'error' ? 'error' : dash.data ? 'success' : 'loading'
  return {
    items,
    dashboard: dash.data,
    status,
    error: dash.error,
    refetch: () => {
      dash.refetch()
      list.refetch()
      inquiries.refetch()
    },
  }
}
